import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Box} from '@mui/material';
import {t} from 'i18next';
import {useDispatch} from 'react-redux';
import './CreateNewProtocolModal.scss';
import ArrModal from '../../../../../common/arr-modal/ArrModal';
import ArrTextField from '../../../../../common/arr-text-field/ArrTextField';
import {useValidateProtocolName} from '../../../../../../hooks/ai/review/useReview';
import {showToast} from '../../../../../../redux/slice/aiReviewSliceV2';
import {ToastType} from '../../../../../../shared/constants';

export const PROTOCOL_NAME_MAX_LENGTH = 50;

export type ProtocolNameValidationError = 'duplicate';

export const isDuplicateProtocolName = (
  name: string,
  existingNames: string[]
): boolean => {
  const normalized = name.trim().toLowerCase();
  if (!normalized) {
    return false;
  }

  return existingNames.some(
    (existing) => existing.trim().toLowerCase() === normalized
  );
};

export const getProtocolNameValidationError = (
  name: string,
  existingNames: string[],
  checkDuplicate = false
): ProtocolNameValidationError | null => {
  if (checkDuplicate && isDuplicateProtocolName(name, existingNames)) {
    return 'duplicate';
  }

  return null;
};

export const getProtocolNameValidationMessage = (
  error: ProtocolNameValidationError | null
): string | undefined => {
  if (error === 'duplicate') {
    return t('ai.review.jobDetailsTab.saveAsNewProtocolDuplicateError');
  }
  return undefined;
};

const isConflictError = (error: unknown): boolean => {
  const apiError = error as {status?: number; response?: {status?: number}};
  return apiError?.status === 409 || apiError?.response?.status === 409;
};

export const isDuplicateNameResponse = (
  isDuplicate?: string | null
): boolean => isDuplicate?.trim().toLowerCase() === 'yes';

interface CreateNewProtocolModalProps {
  open: boolean;
  onClose: () => void;
  onContinue: (protocolName: string) => void;
  existingProtocolNames?: string[];
  description?: string;
  textFieldLabel?: string;
}

const CreateNewProtocolModal = ({
  open,
  onClose,
  onContinue,
  existingProtocolNames = [],
  description = t('ai.review.jobDetailsTab.createNewProtocolDescription'),
  textFieldLabel = t('ai.review.jobDetailsTab.createNewProtocolNameLabel'),
}: CreateNewProtocolModalProps) => {
  const dispatch = useDispatch();
  const [protocolName, setProtocolName] = useState('');
  const [validationError, setValidationError] =
    useState<ProtocolNameValidationError | null>(null);

  const {mutateAsync: validateName, isPending: isValidating} =
    useValidateProtocolName();

  const resetForm = useCallback(() => {
    setProtocolName('');
    setValidationError(null);
  }, []);

  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open, resetForm]);

  const handleClose = () => {
    if (isValidating) {
      return;
    }
    resetForm();
    onClose();
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {value} = event.target;
    setProtocolName(value);

    const error = getProtocolNameValidationError(value, existingProtocolNames);
    setValidationError(error);
  };

  const handleContinue = async () => {
    const trimmedName = protocolName.trim();
    if (!trimmedName || isValidating) {
      return;
    }

    const clientError = getProtocolNameValidationError(
      trimmedName,
      existingProtocolNames,
      true
    );
    if (clientError) {
      setValidationError(clientError);
      return;
    }

    try {
      const result = await validateName({protocolName: trimmedName});
      if (isDuplicateNameResponse(result?.isDuplicate)) {
        setValidationError('duplicate');
        return;
      }
      onContinue(trimmedName);
      resetForm();
    } catch (error) {
      // Fallback: some environments may still return HTTP 409
      if (isConflictError(error)) {
        setValidationError('duplicate');
        return;
      }

      dispatch(
        showToast({
          title: t('common.error'),
          message: t(
            'ai.review.jobDetailsTab.validateProtocolNameErrorToast'
          ),
          status: [ToastType.Error],
        })
      );
    }
  };

  const helperText = useMemo(() => {
    const errorMessage = getProtocolNameValidationMessage(validationError);
    if (errorMessage) {
      return errorMessage;
    }
    return `${protocolName.length}/${PROTOCOL_NAME_MAX_LENGTH}`;
  }, [protocolName.length, validationError]);

  const isContinueDisabled =
    !protocolName.trim() || validationError !== null || isValidating;

  return (
    <ArrModal
      className="arr-create-new-protocol-modal"
      showModal={open}
      onClose={handleClose}
      showClose={false}
      title={t('ai.review.jobDetailsTab.createNewProtocolTitle')}
      description={description}
      aria-label={t('ai.review.jobDetailsTab.createNewProtocolTitle')}
      dataTestId="create-new-protocol-modal"
      buttons={[
        {
          id: 'cancel-btn',
          label: t('ai.review.leftPanel.cancelBtn'),
          onClick: handleClose,
          variant: 'secondary',
          disabled: isValidating,
          dataTestId: 'create-new-protocol-cancel-button',
        },
        {
          id: 'continue-btn',
          label: t('ai.review.jobDetailsTab.continue'),
          onClick: handleContinue,
          variant: 'primary',
          disabled: isContinueDisabled,
          loading: isValidating,
          dataTestId: 'create-new-protocol-continue-button',
        },
      ]}
    >
      <Box className="create-new-protocol-name-field">
        <ArrTextField
          label={textFieldLabel}
          value={protocolName}
          onChange={handleChange}
          state={validationError ? 'error' : 'enabled'}
          showErrorIcon={Boolean(validationError)}
          supportText={helperText}
          helperText={helperText}
          dataTestId="create-new-protocol-name-input"
          slotProps={{
            input: {
              inputProps: {maxLength: PROTOCOL_NAME_MAX_LENGTH},
            },
            formHelperText: {
              'aria-label': validationError
                ? getProtocolNameValidationMessage(validationError)
                : `${protocolName.length} ${t('ai.review.jobDetailsTab.saveAsNewProtocolOutOf50Characters')}`,
            },
          }}
        />
      </Box>
    </ArrModal>
  );
};

export default CreateNewProtocolModal;
